import { Alert, type ColorPaletteProp, Typography } from '@mui/joy';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { type DuplicateCase, getDuplicateCaseLine } from '@/shared/utils/duplicateCase';
import { OpenIcon } from '@/shared/icons/Icons';
import { routeFactories } from '@/containers/App/Router/Routes';

const MAX_VISIBLE_CASES = 5;

type DuplicateCaseListAlertProps = {
  cases: DuplicateCase[];
  color: ColorPaletteProp;
  startDecorator: React.ReactNode;
  title: string;
};

/**
 * Renders every matching duplicate case as a single alert instead of one alert per case: lists up
 * to five cases and summarizes the rest as "and N more cases".
 *
 * @param props.cases - Matching cases, as returned by `useDuplicateAddressCases`/`useDuplicateReferenceCases`.
 * @param props.color - Alert color.
 * @param props.startDecorator - Alert icon.
 * @param props.title - Warning text shown above the case list.
 */
const DuplicateCaseListAlert: React.FC<DuplicateCaseListAlertProps> = ({
  cases,
  color,
  startDecorator,
  title,
}) => {
  const { t } = useTranslation();
  const visibleCases = cases.slice(0, MAX_VISIBLE_CASES);
  const hiddenCasesCount = cases.length - visibleCases.length;

  return (
    <Alert color={color} startDecorator={startDecorator}>
      <div className="flex flex-col">
        <Typography level="title-sm">{title}</Typography>
        {visibleCases.map((duplicateCase) => (
          <div key={duplicateCase.id} className="flex items-center gap-1">
            <Typography level="body-sm">{getDuplicateCaseLine(duplicateCase)}</Typography>
            <Link target="_blank" to={routeFactories.caseDetail({ caseId: duplicateCase.id })}>
              <OpenIcon />
            </Link>
          </div>
        ))}
        {!!hiddenCasesCount && (
          <Typography level="body-sm">
            {t('duplicateCaseCheck.andMoreCases', { count: hiddenCasesCount })}
          </Typography>
        )}
      </div>
    </Alert>
  );
};

export default DuplicateCaseListAlert;
